import {useEffect, useState} from "react"

import {getHostBridge} from "@frontend/hostContract"
import {trpc} from "@frontend/utils/trpc"

import {
  buildReplacementEstimateContext,
  resolveHostReplacementEstimate,
} from "./appHostFunctions"
import type {CaseInfoInput} from "./appHostFunctions"

export const isHostReplacementEstimateEnabled = (): boolean =>
  import.meta.env.VITE_HOST_REPLACEMENT_ESTIMATE_ENABLED !== "false"

export interface HostReplacementEstimateState {
  estimate: ReplacementEstimate | null
  isLoading: boolean
}

/**
 * Resolve a host replacement estimate, log the exchange, and return it
 * so callers can refetch peek/remediation with the estimate in the request.
 * Falls back to estimate = null (Burke bands) when the callback is missing
 * or the host returns empty/invalid/error/timeout.
 */
export const useHostReplacementEstimate = ({
  enabled,
  caseId,
  product,
}: {
  enabled: boolean
  caseId: string
  product: CaseInfoInput
}): HostReplacementEstimateState => {
  const hostEstimateEnabled = enabled && isHostReplacementEstimateEnabled()
  const hasCallback =
    typeof getHostBridge()?.getReplacementEstimate === "function"
  const logExchange = trpc.logHostDataExchange.useMutation()

  const [state, setState] = useState<HostReplacementEstimateState>(() => ({
    estimate: null,
    isLoading: hostEstimateEnabled && hasCallback,
  }))

  const productKey = JSON.stringify({
    brand: product.brand ?? null,
    modelNumber: product.modelNumber ?? null,
    applianceType: product.applianceType ?? null,
    applianceAge: product.applianceAge ?? null,
    symptom: product.symptom ?? null,
    causes: product.causes ?? null,
    parts: product.parts ?? null,
  })

  useEffect(() => {
    const callbackExists =
      typeof getHostBridge()?.getReplacementEstimate === "function"

    if (!hostEstimateEnabled || !callbackExists) {
      setState({
        estimate: null,
        isLoading: false,
      })
      return
    }

    setState({
      estimate: null,
      isLoading: true,
    })

    let cancelled = false
    const context = buildReplacementEstimateContext({caseId, product})

    void (async () => {
      const resolution = await resolveHostReplacementEstimate(context)
      if (resolution.status !== "skipped") {
        try {
          await logExchange.mutateAsync({
            caseId,
            kind: "replacementEstimate",
            status: resolution.status,
            request: context,
            response: resolution.estimate,
            error: resolution.error,
            durationMs: resolution.durationMs,
          })
        } catch {
          // Logging is best-effort and must not block the live recommendation path.
        }
      }
      if (!cancelled) {
        setState({
          estimate: resolution.estimate,
          isLoading: false,
        })
      }
    })()

    return (): void => {
      cancelled = true
    }
    // productKey captures product fields used to build the host context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostEstimateEnabled, caseId, productKey])

  return state
}
